import { cloneDeep } from "lodash";
import {
  HAS_GLP1,
  LESS_THAN_25LBS,
  MORE_THAN_25LBS,
  MORE_THAN_50LBS,
  PRODUCT_BRANCHING,
  UNSURE_MEDS
} from "config/tags";
import { IMPERIAL, METRIC } from "utils/measurementSystem";
import { getChartData } from "utils/weightLossTrajectory";
import convert, { getWeightLocalized } from "utils/weightUnits";
import { getBMI } from "utils/bmi";
import { findAnswerByQuestionTag } from "utils/questions";
import { ESTIMATED_WEIGHT_LOSS_PERCENTAGE_BY_FOUR_WEEKS } from "components/Results/constants";

const getWeightLossBoundariesAndSystem = () => {
  switch (window.ww.locale) {
    case "fr-CA":
    case "fr-FR":
    case "de-DE":
      return {
        lowerBound: 11,
        upperBound: 22,
        systemToUse: METRIC
      };

    case "en-GB":
      return {
        lowerBound: 1,
        upperBound: 3,
        systemToUse: IMPERIAL
      };

    case "en-US":
    case "en-CA":
    default:
      return {
        lowerBound: 25,
        upperBound: 50,
        systemToUse: IMPERIAL
      };
  }
};

// Shows different text on the "You’re in the right place!" screen
// based on goal weight.
export const getWeightLossResults = (profile, assessment) => {
  const { lowerBound, upperBound, systemToUse } =
    getWeightLossBoundariesAndSystem();
  const { weight, desiredWeight } = profile[systemToUse];
  const weightLoss = Math.round((weight - desiredWeight) * 10) / 10;
  let clonedQuestion = {};

  const lessThanLowerBound = assessment?.questions?.find(q =>
    q.tags.includes(LESS_THAN_25LBS)
  );

  if (weightLoss >= lowerBound && weightLoss < upperBound) {
    const middleBound = assessment?.questions?.find(q =>
      q.tags.includes(MORE_THAN_25LBS)
    );
    clonedQuestion = cloneDeep(lessThanLowerBound);
    clonedQuestion.headerText2 = middleBound.headerText2;
    clonedQuestion.images = middleBound.images;
    return clonedQuestion;
  } else if (weightLoss >= upperBound) {
    const moreThanUpperBound = assessment?.questions?.find(q =>
      q.tags.includes(MORE_THAN_50LBS)
    );
    clonedQuestion = cloneDeep(lessThanLowerBound);
    clonedQuestion.headerText2 = moreThanUpperBound.headerText2;
    clonedQuestion.images = moreThanUpperBound.images;
    return clonedQuestion;
  }
  return lessThanLowerBound;
};

export const getWeightLossChartData = ({
  profile,
  answers = [],
  questions = [],
  showClinicalChart = false
}) => {
  if (!profile || !profile.measurementSystem) {
    return {};
  }

  const metric = profile.measurementSystem === "metric";
  const weightUnit = profile[profile.measurementSystem].weightUnit;
  const weeksPerMonth = 4.3;
  const WEEKS = 25; // max of 6 months.
  const gender = profile.gender === "O" ? "F" : profile.gender; // Use female data for other.
  let isClinical = showClinicalChart;

  // Convert stones to pounds for chart data.
  const desiredWeightLossPounds = Math.floor(
    convert(
      profile.imperial.weight - profile.imperial.desiredWeight,
      profile.imperial.weightUnit,
      "lbs"
    )
  );

  let seriesData = [];
  let estimatedWeightChange;
  let estimatedDate;
  let alternateWeightChange;
  let alternateMonths;
  let categories;

  if (answers.length && questions.length) {
    const productBranchingAnswer = findAnswerByQuestionTag(
      PRODUCT_BRANCHING,
      answers,
      questions
    );
    const hasGlp1Answer = findAnswerByQuestionTag(HAS_GLP1, answers, questions);
    const productUnsureAnswer = answers.find(a =>
      a?.answers?.[0]?.tags.includes(UNSURE_MEDS)
    )?.answers[0].value;

    if (
      productBranchingAnswer === "Yes" ||
      hasGlp1Answer === "Yes" ||
      productUnsureAnswer === "Yes"
    ) {
      isClinical = true;
    }
  }

  if (isClinical) {
    let estimatedWeightLossData =
      ESTIMATED_WEIGHT_LOSS_PERCENTAGE_BY_FOUR_WEEKS.map(
        ([week, estimatedPercentage]) => ({
          week,
          weight: profile?.imperial?.weight * (1 - estimatedPercentage)
        })
      );
    estimatedWeightLossData = estimatedWeightLossData.map(v => v.weight);

    // Filter out weights that are greater than the desired weight loss.
    const maxLoss = Math.round(
      estimatedWeightLossData[0] - Math.min(...estimatedWeightLossData)
    );
    estimatedWeightChange = Math.min(desiredWeightLossPounds, maxLoss);
    seriesData = estimatedWeightLossData.filter(
      weight => weight >= estimatedWeightLossData[0] - estimatedWeightChange
    );

    // Get estimated date to reach the desired weight loss.
    const date = new Date();
    date.setDate(date.getDate() + (seriesData.length - 1) * 4 * 7);
    const estimateDateDay = date.toLocaleString("default", {
      day: "numeric"
    });

    const monthPrefix = !window.ww.locale.includes("en")
      ? ""
      : estimateDateDay < 7
        ? "early "
        : estimateDateDay > 21
          ? "the end of "
          : "mid-";

    estimatedDate =
      monthPrefix +
      date.toLocaleString(window.ww.locale, {
        month: "long",
        year: "numeric"
      });

    // Get graph categories.
    const middle = Math.round(seriesData.length / 2) - 1;
    categories = seriesData.map((v, week) => {
      const now = new Date();
      now.setDate(now.getDate() + week * 4 * 7);
      const month = now.toLocaleString(window.ww.locale, { month: "short" });
      switch (week) {
        case 0:
          return month;
        case middle:
          return seriesData.length > 3 ? month : "";
        case seriesData.length - 1:
          return month;
        default:
          return "";
      }
    });

    alternateMonths =
      Math.round((((seriesData.length - 1) * 4) / weeksPerMonth) * 2) / 2;
    alternateWeightChange = Math.round(seriesData[0] - Math.min(...seriesData));
  } else {
    const {
      results: resultsPoints,
      estimatedWeightChange: estimatedWeightChangePoints
    } = getChartData(
      gender,
      getBMI(
        profile.metric.height,
        profile.metric.weight,
        profile.metric.heightUnit
      ),
      desiredWeightLossPounds
    );

    estimatedWeightChange = estimatedWeightChangePoints;

    const date = new Date();
    date.setDate(date.getDate() + resultsPoints.length * 7);
    const estimateDateDay = date.toLocaleString("default", {
      day: "numeric"
    });

    // Month prefix for English only.
    const monthPrefix = !window.ww.locale.includes("en")
      ? ""
      : estimateDateDay < 7
        ? "early "
        : estimateDateDay > 21
          ? "the end of "
          : "mid-";

    estimatedDate =
      monthPrefix +
      date.toLocaleString(window.ww.locale, {
        month: "long",
        year: "numeric"
      });

    const chartData = resultsPoints.slice(0, WEEKS);

    seriesData = chartData.map(v => {
      const loss = convert(
        v,
        "lbs",
        profile[profile.measurementSystem].weightUnit
      );
      return metric
        ? profile.metric.weight + loss
        : profile.imperial.weight + loss;
    });

    alternateMonths = Math.round((seriesData.length / weeksPerMonth) * 2) / 2; // round to nearest 0.5 months.
    alternateWeightChange = seriesData[0] - Math.min(...seriesData);

    const middle = Math.round(seriesData.length / 2) - 1;
    categories = seriesData.map((v, week) => {
      const now = new Date();
      now.setDate(now.getDate() + (week + 1) * 7);
      const month = now.toLocaleString(window.ww.locale, { month: "short" });
      switch (week) {
        case 0:
          return month;
        case middle:
          return seriesData.length > 8 ? month : "";
        case seriesData.length - 1:
          return month;
        default:
          return "";
      }
    });
  }

  const { numberFormatted, graphUnit } = getWeightLocalized(
    profile,
    alternateWeightChange
  );

  alternateWeightChange = numberFormatted;

  const maxLoss = Math.min(...seriesData) - seriesData[0];

  const predictedWeight = metric
    ? profile.metric.weight + maxLoss
    : profile.imperial.weight + maxLoss;

  const weightLoss =
    estimatedWeightChange < desiredWeightLossPounds
      ? alternateWeightChange
      : `${desiredWeightLossPounds} lbs`;

  const weightLossDate =
    estimatedWeightChange < desiredWeightLossPounds
      ? `in your first ${alternateMonths} months`
      : `by ${estimatedDate}`;

  return {
    predictedWeight,
    seriesData,
    graphUnit,
    weightUnit,
    maxLoss,
    desiredWeightLossPounds,
    estimatedWeightChange,
    estimatedDate,
    alternateWeightChange,
    alternateMonths,
    categories,
    weightLoss,
    weightLossDate
  };
};
